import {
  Box,
  MenuItem,
  Select,
  Stack,
  Typography,
  Button,
  Link,
  TextField,
  Tooltip,
  Divider,
  Container,
  Menu,
  IconButton,
  Skeleton,
} from "@mui/material";
import CustomTable from "global/components/CustomTable/CustomTable";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import {
  contactsOptions,
  contactsTableHeader,
  StatusColors,
} from "screens/Contacts/ContactsConstants";
import { useEffect, useRef, useState } from "react";
import {
  convertESTtoUserLocalDateAndTime,
  debounceEventHandler,
  getFormattedStatsCount,
  handleErrorMessage,
  initialPageFilterState,
  isTruthy,
  openSuccessNotification,
  validatePageQuery,
  validatePerPageQuery,
} from "helpers/methods";
import {
  contactsStatus,
  deleteArchiveContact,
  exportContacts,
  fetchAllContacts,
  fetchAllContactsCount,
  softDeleteContact,
} from "./ContactsService";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import SearchIcon from "assets/icons1/search_icon.svg";
import contactsStyles from "./Contacts.styles";
import AddContactsModal from "./AddContacts/AddContactsModal";
import { useTheme } from "@mui/material/styles";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { RouterLink } from "components/router-link";
import strings from "global/constants/StringConstants";
import { useAppSelector } from "utils/hooks";
import { selectIsManager } from "redux/generalSlice";
import ExportIcon from "assets/icons1/export_csv_icon.svg";
import { hasAccessTo, isAdmin } from "layouts/Config";
import Unauthorized from "screens/Shared/Unauthorized";
import { pageFilters } from "models/interfaces";
import { useLocation } from "react-router-dom";
import CustomInfoTooltip from "global/components/CustomInfoTooltip/CustomInfoTooltip";
import ConfirmationModal from "screens/Templates/Components/ConfirmationModal";
import {
  subscribeContactsById,
  unsubscribeMultiContactsById,
} from "./ViewContact/ViewContactService";
import { cloneDeep } from "lodash";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { MoreActionsMenu } from "screens/Contacts/ContactsConstants";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EmailSubscriptionDialog from "./EmailSubscriptionDialog";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";

const Contacts = () => {
  useTitle(title.CONTACTS);
  const theme = useTheme();
  const classes = contactsStyles(theme);
  const isManager = useAppSelector(selectIsManager);
  const [contacts, setContacts] = useState<any>();
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [addContactsModal, setAddContactsModal] = useState<boolean>(false);
  const [isUnsubscribeContactModal, setIsUnsubscribeContactModal] =
    useState<boolean>(false);
  const [isEmailStatusChangeModal, setIsEmailStatusChangeModal] =
    useState<boolean>(false);
  const searchTerm = useRef<any>(null);
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const contactTypeFromUrl = urlParams.get("category");
  const searchFromUrl = urlParams.get("search");
  const contactStatusFromUrl = urlParams.get("status");
  const [isExportContactModel, setIsExportContactModel] =
    useState<boolean>(false);
  const [selectedContactIds, setSelectedContactIds] = useState<
    { id: number; status: number }[]
  >([]);
  const [contactsFilters, setContactsFilters] = useState<pageFilters>(
    initialPageFilterState(
      pageFromUrl,
      rowsPerPageFromUrl,
      searchFromUrl ?? "",
      contactTypeFromUrl ?? "all",
      contactStatusFromUrl ?? "-1"
    )
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [moreOptionsMetadata, setMoreOptionsMetadata] = useState<{
    status: number;
  }>({ status: 0 });
  const [contactId, setContactId] = useState<{ id: number; status: number }>({
    id: 0,
    status: 0,
  });
  const [isSubscribeContactModal, setIsSubscribeContactModal] =
    useState<boolean>(false);
  const [singleContactModal, setSingleContactModal] = useState<boolean>(false);
  const [contactDeleteModal, setContactDeleteModal] = useState<boolean>(false);
  const [singleContactDeleteModal, setSingleContactDeleteModal] =
    useState<boolean>(false);
  const [contactsStatusOptions, setContactsStatusOptions] = useState([
    {
      id: -1,
      name: "All status",
    },
  ]);

  useEffect(() => {
    const { page, rowsPerPage, searchText, dropdownValue, status } =
      contactsFilters;
    if (searchTerm.current !== null) searchTerm.current.value = searchText;
    window.history.replaceState(
      null,
      "",
      `?page=${page}&rowsPerPage=${rowsPerPage}&category=${dropdownValue}&status=${status}`
    );
    getTableData();
  }, [contactsFilters]);

  const hasContactAddPermission = hasAccessTo(
    strings.contact,
    strings.addPermission
  );

  const hasContactFetchPermission = hasAccessTo(
    strings.contact,
    strings.fetchPermission
  );

  const hasContactUpdatePermission = hasAccessTo(
    strings.contact,
    strings.updatePermission
  );

  const getContactStatusOptions = async () => {
    try {
      setIsLoading(true);
      const response = await contactsStatus();
      const filteredResponse = response.filter((item: any) => item.id !== 4);
      setContactsStatusOptions([
        { id: -1, name: "All status" },
        ...filteredResponse,
      ]);
      return filteredResponse;
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getTableData = async () => {
    const { searchText, dropdownValue, page, rowsPerPage, status } =
      contactsFilters;
    try {
      setIsLoading(true);
      const count = await fetchAllContactsCount(
        dropdownValue!,
        searchText,
        +status!
      );
      setCount(count);
      const response = await getContactStatusOptions();
      await getTableDataAccordingToPage(
        count,
        dropdownValue!,
        page,
        searchText.trim(),
        rowsPerPage,
        +status!,
        response
      );
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTableDataAccordingToPage = async (
    count: number,
    dropdownValue: string,
    page: number,
    searchText: string,
    rowsPerPage: number,
    status: number,
    contactsStatusOptions: any
  ) => {
    try {
      let contactsRes: any[] = [];
      setIsLoading(true);
      contactsRes = await fetchAllContacts(
        page,
        rowsPerPage,
        dropdownValue,
        searchText,
        status
      );
      const tableData = convertDataToTableFormat(
        contactsRes,
        contactsStatusOptions
      );
      setContacts([...tableData]);
    } catch (error: any) {
      setIsLoading(false);
      throw error;
    }
  };

  const dropdownHandler = (event: any) => {
    setContactsFilters({
      ...contactsFilters,
      page: 1,
      dropdownValue: event.target.value,
    });
    setSelectedContactIds([]);
     setContactId({
      id: 0,
      status: 0,
    });
  };

  const statusChangeHandler = (event: any) => {
    setContactsFilters({
      ...contactsFilters,
      page: 1,
      status: event.target.value,
    });
    setSelectedContactIds([]);
    setContactId({
      id: 0,
      status: 0,
    });
  };

  const searchChangeHandler = (event: any) => {
    const searchText = event.target.value;
    setContactsFilters({
      ...contactsFilters,
      page: 1,
      searchText,
    });
  };

  const pageChangeHandler = (event: any, newPage: number) => {
    setContactsFilters({
      ...contactsFilters,
      page: newPage,
    });
  };

  const perPageRowsChangeHandler = (rowPerPage: number) => {
    setContactsFilters({
      ...contactsFilters,
      rowsPerPage: rowPerPage,
      page: 1,
    });
  };

  const getExportContacts = async () => {
    try {
      const response = await exportContacts();
      openSuccessNotification(response.message);
      setIsExportContactModel(false);
    } catch (error: any) {
      setIsLoading(false);
      setIsExportContactModel(false);
      handleErrorMessage(error);
    }
  };

  const resetStates = () => {
    setIsSubscribeContactModal(false);
    setSingleContactModal(false);
    setSelectedContactIds([]);
    setSingleContactDeleteModal(false);
    setContactDeleteModal(false);
    setIsUnsubscribeContactModal(false);
    setContactId({
      id: 0,
      status: 0,
    });
    setContactsFilters({
      ...contactsFilters,
      page: 1,
    });
  };

  const handleDialogClose = () => {
    setAddContactsModal(false);
  };

  const handleOpenMenu = (event: any, status: number, id: number) => {
    setAnchorEl(event.currentTarget);
    setMoreOptionsMetadata({
      status,
    });
    setContactId({ id, status });
  };

  const handleMenuItemClick = (action: string) => {
    switch (action) {
      case "unsubscribe":
        setAnchorEl(null);
        setSingleContactModal(true);
        setIsUnsubscribeContactModal(true);
        break;
      case "subscribe":
        setAnchorEl(null);
        setIsSubscribeContactModal(true);
        break;
      case "delete":
        setAnchorEl(null);
        setSingleContactDeleteModal(true);
        break;
      default:
        break;
    }
  };

  const getAddContactsModal = () => {
    return (
      <AddContactsModal
        handleDialogClose={() => handleDialogClose()}
        isVisible={addContactsModal}
        filters={contactsFilters}
        setFilters={setContactsFilters}
      />
    );
  };

  const convertDataToTableFormat = (data: any, contactsStatusOptions: any) => {
    return data.map((contact: any) => {
      const fullName = [contact.firstName, contact.lastName]
        .filter(Boolean)
        .join(" ");
      const status =
        isTruthy(data) &&
        contactsStatusOptions.find((item: any) => item.id === contact.status)
          ?.name;

      return {
        rowId: contact.id,
        email: (
          <Stack direction="column">
            <Link
              color="text.primary"
              variant="subtitle2"
              href={`${viewpaths.viewContactViewPath}/${contact.id}`}
              component={RouterLink}
            >
              {contact.emailId}
            </Link>
          </Stack>
        ),
        name: (
          <Tooltip
            title={fullName ? fullName : "N/A"}
            arrow
            placement="top-start"
          >
            <Typography sx={{ color: "#475569" }} variant="subtitle2">
              {fullName
                ? fullName.length > 20
                  ? `${fullName.slice(0, 20)}...`
                  : fullName
                : "N/A"}
            </Typography>
          </Tooltip>
        ),
        status: (
          <Typography
            sx={{
              color: StatusColors[contact.status],
              display: "flex",
              alignItems: "center",
            }}
            variant="subtitle2"
          >
            <Box
              sx={{
                ...classes.contactStatusBox,
                backgroundColor: StatusColors[contact.status],
              }}
            />
            {status ? status : "N/A"}
          </Typography>
        ),
        statusRow: contact.status,
        createdOn: (
          <Typography sx={{ color: "#475569" }} variant="subtitle2">
            {convertESTtoUserLocalDateAndTime(
              contact.createdOn,
              "MMMM D, YYYY [at] h:mm A"
            )}
          </Typography>
        ),
        action: (
          <Stack direction="row" spacing={4} mt={1} alignItems="center">
            <IconButton
              size="small"
              sx={{
                color: theme.palette.primary.dark,
                padding: 0,
              }}
              onClick={(e: any) => {
                e.stopPropagation();
                handleOpenMenu(e, contact.status, contact.id);
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Stack>
        ),
      };
    });
  };

  const getFilters = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Stack>
          <TextField
            id="contacts_search_box"
            placeholder="Search Contacts"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.MuiOutlinedInput-root.Mui-focused": {
                border: "none !important",
              },
            }}
            autoComplete="off"
            fullWidth
            onChange={debounceEventHandler(
              searchChangeHandler,
              strings.SEARCH_TIME_OUT
            )}
            inputRef={searchTerm}
            InputProps={{
              startAdornment: (
                <Stack mr={1}>
                  <img src={SearchIcon} width="30px" height="20px" />
                </Stack>
              ),
            }}
          />
          <>
            <Divider />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row">
                {isManager && (
                  <Select
                    variant="outlined"
                    id="contacts_category_dropdown"
                    value={contactsFilters.dropdownValue}
                    onChange={dropdownHandler}
                    sx={classes.dropDown}
                    disabled={isLoading}
                  >
                    {contactsOptions.map((data, index: number) => (
                      <MenuItem
                        key={index}
                        value={data.value}
                        sx={{
                          ...classes.dropDownMenuItem,
                          padding: "12px",
                        }}
                      >
                        {data.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                <Select
                  variant="outlined"
                  id="contacts_category_dropdown"
                  value={contactsFilters.status}
                  onChange={statusChangeHandler}
                  sx={classes.dropDown}
                  disabled={isLoading}
                >
                  {isTruthy(contactsStatusOptions) &&
                    contactsStatusOptions.map((data: any, index: number) => (
                      <MenuItem
                        key={index}
                        value={data.id}
                        sx={{
                          ...classes.dropDownMenuItem,
                          padding: "12px",
                        }}
                      >
                        {data.name}
                      </MenuItem>
                    ))}
                </Select>
              </Stack>

              {isAdmin() && (
                <Box>
                  <Button
                    id="unsubscribe_btn"
                    variant="text"
                    size="small"
                    startIcon={
                      <Stack>
                        <ArchiveOutlinedIcon
                          sx={{ color: "#475569", fontSize: "18px" }}
                        />
                      </Stack>
                    }
                    sx={{
                      "& .MuiButton-startIcon": {
                        marginRight: "2px",
                        opacity: isLoading || count === 0 ? 0.5 : 1,
                      },
                    }}
                    disabled={isLoading || count === 0}
                    onClick={() =>
                      history.push(viewpaths.archivedContactsViewPath)
                    }
                  >
                    View archived contacts
                  </Button>
                  <Button
                    id="download_csv_btn"
                    variant="text"
                    size="small"
                    startIcon={
                      <Stack>
                        <img
                          src={ExportIcon}
                          alt="Export CSV"
                          style={{
                            opacity: isLoading || count === 0 ? 0.2 : 1,
                          }}
                        />
                      </Stack>
                    }
                    sx={{
                      "& .MuiButton-startIcon": {
                        marginRight: "2px",
                        opacity: isLoading || count === 0 ? 0.5 : 1,
                      },
                    }}
                    disabled={isLoading || count === 0}
                    onClick={() => setIsExportContactModel(true)}
                  >
                    Export contacts
                  </Button>
                </Box>
              )}
            </Stack>
          </>
        </Stack>
      </CenteredCardLayout>
    );
  };

  const getAddImportContactButton = () => {
    return (
      <>
        <CustomInfoTooltip
          infoMessage={
            hasContactAddPermission
              ? ""
              : "You don't have permission to add a contact. Please contact your account administrator."
          }
          placement="top"
        >
          <Button
            id="contact_add_btn"
            variant="outlined"
            disabled={isLoading}
            onClick={() => hasContactAddPermission && setAddContactsModal(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              opacity: hasContactAddPermission ? 1 : 0.5,
            }}
          >
            Create a contact
          </Button>
        </CustomInfoTooltip>
        <CustomInfoTooltip
          infoMessage={
            hasContactAddPermission
              ? ""
              : "You don't have permission to import contacts. Please contact your account administrator."
          }
          placement="top"
        >
          <Button
            id="contact_add_btn"
            variant="contained"
            disabled={isLoading}
            onClick={() =>
              hasContactAddPermission &&
              history.push(viewpaths.importantContactsViewPath)
            }
            sx={{
              display: "flex",
              alignItems: "center",
              opacity: hasContactAddPermission ? 1 : 0.5,
            }}
          >
            Import contacts
          </Button>
        </CustomInfoTooltip>
      </>
    );
  };

  const noContactBox = () => {
    return (
      <Stack alignItems="center" justifyContent="center" textAlign="center">
        <Typography
          variant="h5"
          color={theme.palette.primary.main}
          gutterBottom
        >
          No Contacts
        </Typography>
        <Container maxWidth="sm">
          <Typography variant="body2">
            Your future customer is just one contact away. Make the move! 😉
          </Typography>
        </Container>
        <Stack direction="row" spacing={2} mt={2}>
          {getAddImportContactButton()}
        </Stack>
      </Stack>
    );
  };

  const handleUnsubscribe = async (
    contactData: { id: number; status: number }[]
  ) => {
    try {
      setLoading(true);
      const ids = contactData
        .filter((contact) => contact.status === 1)
        .map((contact) => contact.id);
      const res = await unsubscribeMultiContactsById(ids);
      openSuccessNotification(res.message);
      resetStates();
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleSubscribe = async (contactId: number) => {
    try {
      setLoading(true);
      const res = await subscribeContactsById(contactId);
      openSuccessNotification(res.message);
      resetStates();
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleDelete = async (
    contactData: { id: number; status: number }[]
  ) => {
    try {
      setLoading(true);
      let res;
      const ids = contactData.map((contact) => contact.id);
      const hasArchivedContacts = contactData.some(
        (contact) => contact.status === 4
      );
      res = await deleteArchiveContact(ids);
      if (+contactsFilters.status! === 4 || hasArchivedContacts) {
      } else {
        res = await softDeleteContact(ids);
      }
      openSuccessNotification(res.message);
      resetStates();
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const onSelectOne = (id: number, status: number) => {
    const selectedIds = cloneDeep(selectedContactIds);
    selectedIds.push({ id, status });
    setSelectedContactIds(selectedIds);
  };

  const onDeselectOne = (id: number) => {
    setSelectedContactIds(
      selectedContactIds.filter((select) => select.id !== id)
    );
  };

  const ActoinBtns = () => {
    return (
      <Box display="flex" justifyContent="flex-start">
        {(+contactsFilters.status! === 0 ||
          +contactsFilters.status! === 1 ||
          +contactsFilters.status! === -1) && (
          <Button
            id="unsubscribe_btn"
            variant="text"
            size="small"
            startIcon={
              <Stack>
                <MailOutlineIcon sx={{ color: "#475569", fontSize: "18px" }} />
              </Stack>
            }
            sx={{
              "& .MuiButton-startIcon": {
                marginRight: "2px",
                opacity: isLoading || count === 0 ? 0.5 : 1,
              },
            }}
            disabled={isLoading || count === 0}
            onClick={() => setIsEmailStatusChangeModal(true)}
          >
            Email subscription
          </Button>
        )}

        <Button
          id="delete_btn"
          variant="text"
          size="small"
          startIcon={
            <Stack>
              <DeleteOutlineIcon sx={{ color: "#475569", fontSize: "18px" }} />
            </Stack>
          }
          sx={{
            "& .MuiButton-startIcon": {
              marginRight: "2px",
              opacity: isLoading || count === 0 ? 0.5 : 1,
            },
          }}
          disabled={isLoading || count === 0}
          onClick={() => setContactDeleteModal(true)}
        >
          Delete contact(s)
        </Button>
      </Box>
    );
  };

  const getContactsPage = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Stack spacing={1}>
            <Typography variant="h5" color="text.primary">
              Contacts
            </Typography>
            {isLoading ? (
              <Skeleton
                animation="wave"
                variant="text"
                height={25}
                width={210}
              />
            ) : (
              <Typography variant="body1" color="text.primary">
                {+contactsFilters.status! === -1
                  ? `${getFormattedStatsCount(count)} total contacts`
                  : `${getFormattedStatsCount(count)} ${
                      contactsStatusOptions.find(
                        (item: any) => item.id === +contactsFilters.status!
                      )?.name ?? ""
                    } contacts`}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={1.5}>
            {getAddImportContactButton()}
          </Stack>
        </Stack>
        {getFilters()}
        <CenteredCardLayout removeLastChildBottomPadding={true}>
          <Box>
            <CustomTable
              headers={contactsTableHeader()}
              customHeader={
                selectedContactIds.length > 0 ? ActoinBtns() : undefined
              }
              rows={contacts}
              page={contactsFilters.page}
              rowsPerPage={contactsFilters.rowsPerPage}
              onRowsPerPageChange={perPageRowsChangeHandler}
              onPageChange={pageChangeHandler}
              count={count}
              showJumpToPageNumber={false}
              handleOnChangePageInput={pageChangeHandler}
              showPagination={true}
              isLoading={isLoading}
              isNoDataShow={true}
              noDataComponent={noContactBox()}
              showCheckbox={hasContactUpdatePermission}
              onSelectOne={(id: number) => {
                const contactData = contacts.find(
                  (contact: any) => contact.rowId === id
                );
                if (contactData) {
                  onSelectOne(id, contactData?.statusRow);
                }
              }}
              onDeselectOne={onDeselectOne}
              onSelectAll={() => {
                const pageContactsData = contacts.map((row: any) => ({
                  id: row.rowId,
                  status: row.statusRow,
                }));
                setSelectedContactIds((prev) => {
                  const existingIds = prev.map((item) => item.id);
                  const newContacts = pageContactsData.filter(
                    (contact: any) => !existingIds.includes(contact.id)
                  );
                  return [...prev, ...newContacts];
                });
              }}
              onDeselectAll={() => {
                const pageIds = contacts.map((row: any) => row.rowId);
                setSelectedContactIds((prev) =>
                  prev.filter((item) => !pageIds.includes(item.id))
                );
              }}
              selected={selectedContactIds.map((item) => item.id)}
            />
          </Box>
        </CenteredCardLayout>
        {addContactsModal && getAddContactsModal()}
      </Stack>
    );
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const moreOptions = () => {
    const menu = MoreActionsMenu(moreOptionsMetadata);
    return (
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {menu.map((item, index) => (
          <MenuItem
            key={index}
            value={item.value}
            sx={classes.dropDownMenuItem}
            onClick={() => handleMenuItemClick(item.value)}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    );
  };

  return (
    <>
      {hasContactFetchPermission ? (
        <CenteredAuthenticatedLayout>
          {getContactsPage()}
        </CenteredAuthenticatedLayout>
      ) : (
        <Unauthorized permissionDenied={hasContactFetchPermission} />
      )}
      {isExportContactModel && (
        <ConfirmationModal
          isVisible={isExportContactModel}
          handleDialogClose={() => setIsExportContactModel(false)}
          title="Export contacts"
          subtitle2="You're about to export contacts. Do you want to proceed?"
          btn2Title="Export"
          btn1Variant="outlined"
          btn1Title="Cancel"
          btn2Variant="contained"
          btn2OnClick={() => getExportContacts()}
          btn1OnClick={() => setIsExportContactModel(false)}
        />
      )}
      {isUnsubscribeContactModal && (
        <ConfirmationModal
          isVisible={isUnsubscribeContactModal}
          handleDialogClose={() => {
            setIsUnsubscribeContactModal(false);
          }}
          title={
            singleContactModal
              ? "Unsubscribe contact"
              : "Unsubscribe multiple contacts"
          }
          subtitle2={
            singleContactModal
              ? "Are you sure you want to update the contact's status to Unsubscribe?"
              : `Are you sure you want to update the status of the selected ${selectedContactIds.length} contact(s) to Unsubscribe?`
          }
          btn1Title="Cancel"
          btn1Variant="outlined"
          btn2Title="Unsubscribe"
          btn2Variant="contained"
          btn1OnClick={() => {
            setIsUnsubscribeContactModal(false);
            setSingleContactModal(false);
          }}
          btn2OnClick={() => {
            handleUnsubscribe(
              singleContactModal ? [contactId] : selectedContactIds
            );
          }}
        />
      )}
      {isSubscribeContactModal && (
        <ConfirmationModal
          isVisible={isSubscribeContactModal}
          handleDialogClose={() => {
            setIsSubscribeContactModal(false);
          }}
          title="Subscribe contact"
          subtitle2={
            "Are you sure you want to set this contact's status to Subscribed?  By continuing, you confirm that the contact has given consent to receive emails from us."
          }
          btn1Title="Cancel"
          btn1Variant="outlined"
          btn2Title="Subscribe"
          btn2Variant="contained"
          btn1OnClick={() => {
            setIsSubscribeContactModal(false);
          }}
          btn2OnClick={() => {
            handleSubscribe(contactId.id);
          }}
        />
      )}
      {contactDeleteModal && (
        <ConfirmationModal
          isVisible={contactDeleteModal}
          handleDialogClose={() => {
            setContactDeleteModal(false);
          }}
          title={
            selectedContactIds.length === 1
              ? "Delete contact"
              : "Delete multiple contacts"
          }
          subtitle2={`You have selected ${selectedContactIds.length} contact(s) for deletion. This action is permanent and cannot be undone. Are you sure you want to proceed with deleting them?`}
          btn1Title="Cancel"
          btn1Variant="outlined"
          btn2Title="Delete"
          btn2Variant="contained"
          btn1OnClick={() => {
            setContactDeleteModal(false);
          }}
          btn2OnClick={() => {
            handleDelete(selectedContactIds);
          }}
        />
      )}
      {singleContactDeleteModal && (
        <ConfirmationModal
          isVisible={singleContactDeleteModal}
          handleDialogClose={() => {
            setSingleContactDeleteModal(false);
          }}
          title="Delete contact"
          subtitle2={
            "Deleting contact is permanent and cannot be undone. Are you sure you want to delete this contact?"
          }
          btn1Title="Cancel"
          btn1Variant="outlined"
          btn2Title="Delete"
          btn2Variant="contained"
          btn1OnClick={() => {
            setSingleContactDeleteModal(false);
          }}
          btn2OnClick={() => {
            handleDelete([contactId]);
          }}
        />
      )}
      {moreOptions()}
      {isEmailStatusChangeModal && (
        <EmailSubscriptionDialog
          isDialogOpen={isEmailStatusChangeModal}
          handleDialogClose={() => setIsEmailStatusChangeModal(false)}
          selectedContacts={selectedContactIds}
          resetStates={resetStates}
        />
      )}
      <CustomLoader isLoading={loading} />
    </>
  );
};

export default Contacts;
